import React, { useState } from "react";
import { Eye, TrendingUp, Users, Video } from "lucide-react";
import SideBar from "../components/SideBar";
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const Card = ({ children, className = '' }) => (
    <div className={`bg-white shadow-sm rounded-lg p-6 ${className}`}>
        {children}
    </div>
);

const Button = ({ children, variant = 'default', className = '', ...props }) => {
    const baseStyle = 'px-4 py-2 rounded-lg font-medium transition-colors duration-200';
    const variantStyles = {
        default: 'bg-indigo-600 text-white hover:bg-indigo-700',
        outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50',
        ghost: 'text-gray-700 hover:bg-gray-100',
    };

    return (
        <button
            className={`${baseStyle} ${variantStyles[variant]} ${className}`}
            {...props}
        >
            {children}
        </button>
    );
};


const StatCard = ({ title, value, icon: Icon }) => (
    <Card className="flex items-center">
        <div className="p-3 bg-indigo-100 rounded-full mr-4">
            <Icon className="h-6 w-6 text-indigo-600" />
        </div>
        <div>
            <p className="text-sm font-semibold text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-indigo-600">{value}</p>
        </div>
    </Card>
);

const AnalyticsPage = () => {
    const [timeRange, setTimeRange] = useState('7d');

    // Sample data remains the same
    const viewsData = [
        { name: 'Mon', YouTube: 4000, TikTok: 2400, Instagram: 2400 },
        { name: 'Tue', YouTube: 3000, TikTok: 1398, Instagram: 2210 },
        { name: 'Wed', YouTube: 2000, TikTok: 9800, Instagram: 2290 },
        { name: 'Thu', YouTube: 2780, TikTok: 3908, Instagram: 2000 },
        { name: 'Fri', YouTube: 1890, TikTok: 4800, Instagram: 2181 },
        { name: 'Sat', YouTube: 2390, TikTok: 3800, Instagram: 2500 },
        { name: 'Sun', YouTube: 3490, TikTok: 4300, Instagram: 2100 }
    ];

    const contentDistribution = [
        { name: 'Motivational', value: 400 },
        { name: 'Historical Facts', value: 300 },
        { name: 'Bedtime Stories', value: 300 },
        { name: 'Tech News', value: 200 },
        { name: 'Cooking Tips', value: 150 },
        { name: 'Travel', value: 100 }
    ];

    const topPerformingContent = [
        { title: '5 Quick Motivation Tips', platform: 'YouTube', views: 15234, likes: 1200, comments: 89 },
        { title: 'Interesting History Facts', platform: 'TikTok', views: 22456, likes: 3500, comments: 156 },
        { title: 'Bedtime Story: The Magic Forest', platform: 'Instagram', views: 18901, likes: 2300, comments: 134 },
        { title: 'Tech News Roundup', platform: 'YouTube', views: 12567, likes: 980, comments: 45 },
        { title: '3-Minute Healthy Breakfast Ideas', platform: 'TikTok', views: 19876, likes: 2800, comments: 201 }
    ];

    const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

    return (
        <div className="flex h-screen bg-gray-50">
            <SideBar />
            <div className="flex-1 overflow-x-hidden overflow-y-auto p-8">
                <h2 className="text-3xl font-bold mb-6 text-gray-800">Analytics Dashboard</h2>

                <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Time Range:</h3>
                    <div className="flex space-x-4">
                        <Button
                            isActive={timeRange === '7d'}
                            onClick={() => setTimeRange('7d')}
                        >
                            Last 7 days
                        </Button>
                        <Button
                            isActive={timeRange === '30d'}
                            onClick={() => setTimeRange('30d')}
                        >
                            Last 30 days
                        </Button>
                        <Button
                            isActive={timeRange === '90d'}
                            onClick={() => setTimeRange('90d')}
                        >
                            Last 90 days
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <StatCard title="Total Views" value="152,841" icon={Eye} />
                    <StatCard title="Total Followers" value="25,401" icon={Users} />
                    <StatCard title="Videos Published" value="87" icon={Video} />
                    <StatCard title="Avg. Engagement Rate" value="8.7%" icon={TrendingUp} />
                </div>

                <Card className="mb-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-800">Views Over Time</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={viewsData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="YouTube" stroke="#FF0000" />
                            <Line type="monotone" dataKey="TikTok" stroke="#00F2EA" />
                            <Line type="monotone" dataKey="Instagram" stroke="#E1306C" />
                        </LineChart>
                    </ResponsiveContainer>
                </Card>

                <Card className="mb-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-800">Content Distribution</h3>
                    <div className="flex flex-wrap items-center justify-center">
                        <ResponsiveContainer width={300} height={300}>
                            <PieChart>
                                <Pie
                                    data={contentDistribution}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                >
                                    {contentDistribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="mt-4 md:mt-0 md:ml-8">
                            {contentDistribution.map((entry, index) => (
                                <div key={entry.name} className="flex items-center mb-2">
                                    <div className="w-4 h-4 mr-2" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                                    <span>{entry.name}: {entry.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>

                <Card>
                    <h3 className="text-xl font-semibold mb-4 text-gray-800">Top Performing Content</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Likes</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {topPerformingContent.map((content, index) => (
                                <tr key={index}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{content.title}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{content.platform}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{content.views.toLocaleString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{content.likes.toLocaleString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{content.comments.toLocaleString()}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default AnalyticsPage;
